import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'

// ── PUBLIC NOTARY BIO PAGE ─────────────────────────────────────
// Rendered at /bio/:notaryId — viewable by anyone (no login needed).
// This is what a notary shares with title/escrow companies.

const NAVY = '#0D1B3E'
const GOLD = '#C9A84C'
const LIGHT = '#E8E6DF'
const WEBSITE_URL = '/' // LCA homepage, same domain the bio is served from

function getInitials(name) {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?'
}

export default function NotaryBioPage() {
  const { notaryId } = useParams()
  const [profile,  setProfile]  = useState(null)
  const [notary,   setNotary]   = useState(null)
  const [creds,    setCreds]    = useState([])
  const [loading,  setLoading]  = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    let active = true
    async function load() {
      if (!notaryId) { setNotFound(true); setLoading(false); return }
      try {
        const { data: prof } = await supabase
          .from('profiles')
          .select('id, full_name, headshot_url, city, state, bio')
          .eq('id', notaryId)
          .single()
        if (!active) return
        if (!prof) { setNotFound(true); setLoading(false); return }
        setProfile(prof)

        const { data: not } = await supabase
          .from('notaries')
          .select('commission_state, is_vetted, is_elite, is_founder, is_ron, eo_coverage, years_experience, ron_certified, languages, nsa_certifications, total_signings, avg_rating')
          .eq('id', notaryId)
          .single()
        if (active) setNotary(not || null)

        const { data: docs } = await supabase
          .from('notary_docs')
          .select('doc_type, status')
          .eq('notary_id', notaryId)
        if (active) setCreds(docs || [])
      } catch (err) {
        if (active) setNotFound(true)
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => { active = false }
  }, [notaryId])

  const hasApproved = (type) => creds.some(c => c.doc_type === type && c.status === 'approved')

  function buildGlanceBadges() {
    const badges = []
    if (notary?.commission_state) badges.push({ icon: '📜', label: `Commissioned in ${notary.commission_state}` })
    if (hasApproved('eo_insurance')) badges.push({ icon: '🛡️', label: `Bonded & Insured${notary?.eo_coverage ? ` — ${notary.eo_coverage}` : ''}` })
    if (hasApproved('background_check')) badges.push({ icon: '🔍', label: 'Background Checked' })
    if (notary?.years_experience) badges.push({ icon: '⭐', label: `${notary.years_experience} Years Experience` })
    if (notary?.ron_certified) badges.push({ icon: '💻', label: 'RON Certified' })
    if (notary?.languages?.length > 0) {
      const langs = notary.languages.filter(l => l !== 'English')
      if (langs.length > 0) badges.push({ icon: '🗣️', label: `Bilingual: ${langs.join(', ')}` })
    }
    if (notary?.nsa_certifications?.length > 0) {
      notary.nsa_certifications.forEach(cert => {
        const short = cert.replace('National Notary Association', 'NNA').replace('Loan Signing System', 'LSS')
        badges.push({ icon: '🏅', label: short })
      })
    }
    return badges
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F4F2EC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui,-apple-system,sans-serif', color: NAVY }}>
        Loading bio…
      </div>
    )
  }

  if (notFound || !profile) {
    return (
      <div style={{ minHeight: '100vh', background: '#F4F2EC', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, fontFamily: 'system-ui,-apple-system,sans-serif', color: NAVY, textAlign: 'center', padding: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 700 }}>Bio not available</div>
        <div style={{ fontSize: 14, color: '#6B6A64' }}>This notary profile could not be found.</div>
        <a href={WEBSITE_URL} style={{ marginTop: 8, background: GOLD, color: NAVY, fontWeight: 700, fontSize: 14, padding: '10px 18px', borderRadius: 8, textDecoration: 'none' }}>Visit LoanClosingAgent.com</a>
      </div>
    )
  }

  const glance = buildGlanceBadges()
  const ratingNum = notary?.avg_rating ? Math.round(Number(notary.avg_rating)) : 0
  const signings = notary?.total_signings || 0
  const statParts = []
  if (ratingNum > 0) statParts.push(`${notary.avg_rating} rating`)
  if (signings > 0) statParts.push(`${signings.toLocaleString()} signings`)

  const milestoneBadges = []
  if (notary?.is_founder) milestoneBadges.push('🏅 Founder')
  if (notary?.is_vetted)  milestoneBadges.push('★ LCA Vetted')
  if (notary?.is_elite)   milestoneBadges.push('⚡ Elite')
  if (notary?.is_ron)     milestoneBadges.push('💻 RON Notary')

  return (
    <div style={{ minHeight: '100vh', background: '#F4F2EC', fontFamily: 'system-ui,-apple-system,sans-serif', color: NAVY }}>
      {/* ── TOP BAR ── */}
      <div style={{ background: NAVY, padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <a href={WEBSITE_URL} style={{ textDecoration: 'none' }}>
          <span style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', color: GOLD, fontWeight: 700, fontSize: 20, letterSpacing: '0.02em' }}>Loan Closing Agent</span>
        </a>
        <a href={WEBSITE_URL} style={{ color: GOLD, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>Visit our website ↗</a>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        {/* ── HERO ── */}
        <div style={{ background: NAVY, padding: '28px 20px 32px', textAlign: 'center' }}>
          {profile.headshot_url
            ? <img src={profile.headshot_url} alt={profile.full_name} style={{ width: 110, height: 110, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${GOLD}`, display: 'block', margin: '0 auto 14px' }} />
            : <div style={{ width: 110, height: 110, borderRadius: '50%', background: GOLD, color: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38, fontWeight: 700, margin: '0 auto 14px' }}>{getInitials(profile.full_name)}</div>
          }
          <h1 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', color: GOLD, fontSize: 30, fontWeight: 700, margin: '0 0 4px', lineHeight: 1.1 }}>{profile.full_name}</h1>
          {(profile.city || profile.state) && (
            <div style={{ color: LIGHT, fontSize: 14 }}>📍 {[profile.city, profile.state].filter(Boolean).join(', ')}</div>
          )}
          {(ratingNum > 0 || statParts.length > 0) && (
            <div style={{ marginTop: 10 }}>
              {ratingNum > 0 && <div style={{ color: GOLD, fontSize: 18 }} aria-label={`${ratingNum} out of 5 stars`}>{'★'.repeat(ratingNum)}{'☆'.repeat(5 - ratingNum)}</div>}
              {statParts.length > 0 && <div style={{ color: LIGHT, fontSize: 13, marginTop: 4 }}>{statParts.join(' · ')}</div>}
            </div>
          )}
          {milestoneBadges.length > 0 && (
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap', marginTop: 14 }}>
              {milestoneBadges.map((label, i) => (
                <span key={i} style={{ background: i === 0 ? GOLD : 'rgba(201,168,76,0.15)', color: i === 0 ? NAVY : GOLD, border: i === 0 ? 'none' : `1px solid ${GOLD}`, fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20 }}>{label}</span>
              ))}
            </div>
          )}
        </div>

        {/* ── ABOUT ── */}
        {profile.bio && (
          <section style={{ background: '#fff', margin: '16px', borderRadius: 14, padding: 20, border: '1px solid #E8E6DF' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px', color: NAVY }}>About</h2>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: '#4A4840', margin: 0, whiteSpace: 'pre-wrap' }}>{profile.bio}</p>
          </section>
        )}

        {/* ── CREDENTIALS AT A GLANCE ── */}
        {glance.length > 0 && (
          <section style={{ background: '#fff', margin: '16px', borderRadius: 14, padding: 20, border: '1px solid #E8E6DF' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px', color: NAVY }}>Credentials at a glance</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {glance.map((b, i) => (
                <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F8F7F4', border: '1px solid #E8E6DF', borderRadius: 20, padding: '6px 14px', fontSize: 13, color: NAVY, fontWeight: 500 }}>
                  <span>{b.icon}</span><span>{b.label}</span>
                </span>
              ))}
            </div>
          </section>
        )}

        {/* ── CALL TO ACTION ── */}
        <section style={{ background: '#fff', margin: '16px', borderRadius: 14, padding: '24px 20px', border: '1px solid #E8E6DF', textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: '#4A4840', marginBottom: 12 }}>Part of the Loan Closing Agent vetted notary network</div>
          <a href={WEBSITE_URL} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: GOLD, color: NAVY, fontWeight: 700, fontSize: 14, padding: '11px 20px', borderRadius: 8, textDecoration: 'none' }}>Visit LoanClosingAgent.com →</a>
        </section>
      </div>

      {/* ── FOOTER ── */}
      <div style={{ background: NAVY, padding: '16px 18px', textAlign: 'center', marginTop: 8 }}>
        <div style={{ color: LIGHT, fontSize: 12, lineHeight: 1.6 }}>Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network</div>
      </div>
    </div>
  )
}
